import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { updateParticipation } from '@api/public_endpoints';
import { useFeature } from '@hooks/useFeature';

import DefaultLayout from './DefaultLayout';
import { AutoDisqualified, AutoQualified, ManualReviewQualification } from './Layouts';

function getConfirmationPageKey(screener: Screener, status: string) {
  let key: string;
  if (status == 'requested') {
    key = screener.style == 'video_call' ? 'requested_to_schedule' : 'requested_to_get_started';
  } else if (screener.screener_type === 'pre') {
    key = 'screener';
  } else if (screener.style === 'panel') {
    key = 'panel';
  } else if (screener.style === 'unmoderated_test') {
    key = 'unmoderated';
  } else {
    key = 'survey';
  }
  return key;
}

function getConfirmationType(
  participation: Participation | null
): 'auto_disqualification' | 'auto_qualification' | 'manual_review' | 'default' {
  let type: 'auto_disqualification' | 'auto_qualification' | 'manual_review' | 'default' = 'default';

  if (participation) {
    const lastActivity = participation.activities?.[0];
    if (lastActivity?.key === 'rejected.auto_disqualification') {
      type = 'auto_disqualification';
    } else if (lastActivity?.key === 'requested.auto_qualification') {
      type = 'auto_qualification';
    } else if (participation.status === 'rejected') {
      type = 'auto_disqualification';
    } else if (participation.status === 'requested') {
      type = 'auto_qualification';
    } else {
      type = 'manual_review';
    }
  }

  return type;
}

interface Props {
  screener: Screener;
  participation: Participation | null;
  account: PublicGQAccount;
  previewConfirmationType?: 'auto_qualified' | 'auto_disqualified' | 'manual_review';
}

export const ConfirmationPage: React.FC<React.PropsWithChildren<Props>> = ({
  screener,
  participation,
  account,
  previewConfirmationType
}) => {
  const page = getConfirmationPageKey(screener, participation?.status as ParticipationStatus);
  const publicHasQualificationRules = Array.isArray(account.enabled_features)
    ? account.enabled_features.includes('screeners_qualifications_rules')
    : false;
  const privateHasQualificationRules = useFeature('screeners_qualifications_rules');
  const hasQualificationRules = privateHasQualificationRules || publicHasQualificationRules;
  const { t } = useTranslation('ConfirmationPage');

  async function handleStartClick() {
    if (!participation) return;
    const resp = await updateParticipation(participation.token, { screener_id: screener.id, transition: 'request' });
    if (resp) {
      window.location.href = `/go/${participation.token}`;
    }
  }

  useEffect(() => {
    if (!participation) return;
    const requiredLabel = document.getElementById('required-label');
    if (requiredLabel) {
      requiredLabel.style.visibility = 'hidden';
    }
  }, []);

  const PREVIEW_CONFIRMATION_MAP = {
    auto_qualified: 'auto_qualification',
    auto_disqualified: 'auto_disqualification'
  } as const;

  const confirmationType =
    previewConfirmationType && !participation
      ? (PREVIEW_CONFIRMATION_MAP[previewConfirmationType as keyof typeof PREVIEW_CONFIRMATION_MAP] ?? 'manual_review')
      : getConfirmationType(participation);

  const confirmationComponentMap = {
    manual_review: <ManualReviewQualification t={t} participation={participation} account={account} />,
    auto_disqualification: <AutoDisqualified t={t} participation={participation} account={account} />,
    auto_qualification: (
      <AutoQualified
        t={t}
        page={page}
        previewConfirmationType={previewConfirmationType}
        participation={participation}
        account={account}
        handleStartClick={handleStartClick}
      />
    )
  };

  if (hasQualificationRules && confirmationComponentMap[confirmationType]) {
    return confirmationComponentMap[confirmationType];
  }

  return (
    <DefaultLayout
      t={t}
      page={page}
      participation={participation}
      account={account}
      handleStartClick={handleStartClick}
    />
  );
};
